import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Steps,
  Button,
  Progress,
  Typography,
  Input,
  message,
  Spin,
  Alert,
  Space,
} from 'antd';
import {
  CheckCircleOutlined,
  GlobalOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  ShoppingOutlined,
  ArrowRightOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { BrandService, BrandMaturity, MaturityStep } from '../services/brandService';
import { useAuth } from '../contexts/AuthContext';
import { getActiveBrandId } from '../config/api';
import './BrandMaturityDrawer.css';

const { Title, Text, Paragraph } = Typography;

interface BrandMaturityDrawerProps {
  open: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

const BrandMaturityDrawer: React.FC<BrandMaturityDrawerProps> = ({
  open,
  onClose,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const brandId = getActiveBrandId(user);

  const [maturity, setMaturity] = useState<BrandMaturity | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Form states
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [xmlUrl, setXmlUrl] = useState('');

  useEffect(() => {
    if (open && brandId) {
      loadMaturity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brandId]);

  const loadMaturity = async () => {
    if (!brandId) return;

    try {
      setLoading(true);
      const data = await BrandService.getBrandMaturity(brandId);
      setMaturity(data);

      // Pre-fill form fields with current values
      const websiteStep = data.steps.find(s => s.key === 'website_url');
      const xmlStep = data.steps.find(s => s.key === 'xml_url');
      
      if (websiteStep?.currentValue) {
        setWebsiteUrl(websiteStep.currentValue);
      }
      if (xmlStep?.currentValue) {
        setXmlUrl(xmlStep.currentValue);
      }

      // Find first incomplete step
      const firstIncompleteIndex = data.steps.findIndex(s => !s.completed);
      if (firstIncompleteIndex >= 0) {
        setCurrentStep(firstIncompleteIndex);
      }
    } catch (error) {
      console.error('Failed to load maturity:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStepIcon = (step: MaturityStep, index: number) => {
    if (step.completed) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }

    switch (step.key) {
      case 'website_url':
        return <GlobalOutlined />;
      case 'xml_url':
        return <FileTextOutlined />;
      case 'xml_mapping':
        return <DatabaseOutlined />;
      case 'products':
        return <ShoppingOutlined />;
      default:
        return null;
    }
  };

  const handleSaveWebsiteUrl = async () => {
    if (!brandId || !websiteUrl) {
      message.warning('Lütfen bir URL girin');
      return;
    }

    try {
      setSaving(true);
      await BrandService.updateBrand(brandId, { website: websiteUrl });
      message.success('Website URL kaydedildi');
      await loadMaturity();
      onUpdate?.();
    } catch (error) {
      message.error('Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveXmlUrl = async () => {
    if (!brandId || !xmlUrl) {
      message.warning('Lütfen bir URL girin');
      return;
    }

    try {
      setSaving(true);
      await BrandService.updateBrand(brandId, { xmlFeedUrl: xmlUrl });
      message.success('XML Feed URL kaydedildi');
      await loadMaturity();
      onUpdate?.();
    } catch (error) {
      message.error('Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleNavigate = (url: string) => {
    onClose();
    navigate(url);
  };

  const renderStepContent = (step: MaturityStep) => {
    if (step.completed) {
      return (
        <Alert
          message="Bu adım tamamlandı"
          description={step.currentValue ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {step.key === 'website_url' && `Website: ${step.currentValue}`}
              {step.key === 'xml_url' && `XML Feed: ${step.currentValue}`}
              {step.key === 'xml_mapping' && step.currentValue}
              {step.key === 'products' && step.currentValue}
            </Text>
          ) : undefined}
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
        />
      );
    }

    switch (step.key) {
      case 'website_url':
        return (
          <div className="step-content">
            <Paragraph type="secondary">{step.description}</Paragraph>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="https://www.markaniz.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                prefix={<GlobalOutlined />}
              />
              <Button
                type="primary"
                onClick={handleSaveWebsiteUrl}
                loading={saving}
              >
                Kaydet
              </Button>
            </Space.Compact>
          </div>
        );

      case 'xml_url':
        return (
          <div className="step-content">
            <Paragraph type="secondary">{step.description}</Paragraph>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="https://www.markaniz.com/feed.xml"
                value={xmlUrl}
                onChange={(e) => setXmlUrl(e.target.value)}
                prefix={<FileTextOutlined />}
              />
              <Button
                type="primary"
                onClick={handleSaveXmlUrl}
                loading={saving}
              >
                Kaydet
              </Button>
            </Space.Compact>
          </div>
        );

      case 'xml_mapping':
        return (
          <div className="step-content">
            <Paragraph type="secondary">{step.description}</Paragraph>
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={() => handleNavigate('/products?showMapping=true')}
            >
              XML Mapping Sayfasına Git
            </Button>
          </div>
        );

      case 'products':
        return (
          <div className="step-content">
            <Paragraph type="secondary">{step.description}</Paragraph>
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={() => handleNavigate('/products')}
            >
              Ürünler Sayfasına Git
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  const drawerTitle = (
    <div className="drawer-header">
      <div>
        <Title level={4} style={{ margin: 0 }}>Marka Kurulumu</Title>
        <Text type="secondary">Sistemi kullanmak için gerekli adımları tamamlayın</Text>
      </div>
    </div>
  );

  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      width={480}
      onClose={onClose}
      open={open}
      className="brand-maturity-drawer"
      extra={
        <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
      }
    >
      {loading ? (
        <div className="drawer-loading">
          <Spin size="large" />
        </div>
      ) : maturity ? (
        <div className="drawer-content">
          <div className="progress-section">
            <Progress
              percent={maturity.percentage}
              strokeColor={{
                '0%': '#667eea',
                '100%': '#52c41a',
              }}
              format={(percent) => (
                <span style={{ fontSize: 16, fontWeight: 600 }}>
                  {percent}%
                </span>
              )}
            />
            <Text type="secondary">
              {maturity.completedSteps} / {maturity.totalSteps} adım tamamlandı
            </Text>
          </div>

          {maturity.percentage === 100 ? (
            <Alert
              message="Tebrikler! 🎉"
              description="Tüm kurulum adımlarını tamamladınız. Artık Fashion Connect'in tüm özelliklerini kullanabilirsiniz."
              type="success"
              showIcon
              style={{ marginBottom: 24 }}
            />
          ) : null}

          <Steps
            current={currentStep}
            onChange={setCurrentStep}
            direction="vertical"
            className="maturity-steps"
            items={maturity.steps.map((step, index) => ({
              title: (
                <span className={step.completed ? 'step-title-completed' : ''}>
                  {step.title}
                </span>
              ),
              description: step.completed ? (
                <Text type="success" style={{ fontSize: 12 }}>
                  Tamamlandı
                </Text>
              ) : (
                currentStep === index ? renderStepContent(step) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {step.description}
                  </Text>
                )
              ),
              icon: getStepIcon(step, index),
              status: step.completed ? 'finish' : (currentStep === index ? 'process' : 'wait'),
            }))}
          />
        </div>
      ) : (
        <Alert
          message="Veriler yüklenemedi"
          description="Lütfen sayfayı yenileyip tekrar deneyin."
          type="error"
          showIcon
        />
      )}
    </Drawer>
  );
};

export default BrandMaturityDrawer;
